import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  CheckSquare,
  AlertCircle,
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  Download,
  Eye,
  Loader2,
  ShoppingCart,
  Package,
  ShieldCheck,
  Star,
} from "lucide-react";
import {
  completeMarketplaceCheckout,
  submitMarketplaceReview,
  type MarketplacePackageDetail,
  startMarketplaceCheckout,
  type MarketplacePreview,
  useMarketplacePackageDetail,
  useMarketplacePackagePreview,
  useInstallPackage,
} from "@/hooks/use-marketplace";
import { formatCurrency, formatDate, formatNumber } from "@/lib/i18n";

const CHECKOUT_STORAGE_KEY_PREFIX = "marketplace-checkout:";

function getCheckoutStorageKey(packageId: string) {
  return `${CHECKOUT_STORAGE_KEY_PREFIX}${packageId}`;
}

function formatMoney(priceCents: number, currency: string) {
  if (priceCents === 0) return "Free";
  return formatCurrency(priceCents, currency, { maximumFractionDigits: 0 });
}

function formatPercent(value: number) {
  return `${value.toFixed(1)}%`;
}

function formatRatio(value: number) {
  return `${value.toFixed(1)}x`;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(1)} KB`;
  }

  return `${(kilobytes / 1024).toFixed(1)} MB`;
}

function formatMetricLabel(metric: string) {
  switch (metric) {
    case "conversion":
      return "Conversion";
    case "email_open_rate":
      return "Email open rate";
    case "roas":
      return "ROAS";
    default:
      return metric;
  }
}

function formatContractLabel(value: string) {
  return value.replaceAll("_", " ");
}

function formatOwnershipLabel(assetOwnership: MarketplacePackageDetail["purchaseSemantics"]["assetOwnership"]) {
  switch (assetOwnership) {
    case "buyer_owned_copy":
      return "Buyer-owned copy";
    default:
      return assetOwnership;
  }
}

function formatUpdateLabel(
  creatorUpdatePropagation: MarketplacePackageDetail["purchaseSemantics"]["creatorUpdatePropagation"],
) {
  switch (creatorUpdatePropagation) {
    case "manual_version_offer":
      return "Creator updates are offered manually";
    default:
      return creatorUpdatePropagation;
  }
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-48 animate-pulse rounded bg-muted" />
      <div className="h-64 animate-pulse rounded-xl bg-muted" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="h-80 animate-pulse rounded-xl bg-muted" />
        <div className="h-80 animate-pulse rounded-xl bg-muted" />
      </div>
    </div>
  );
}

type PreviewItem = MarketplacePreview["items"][number];

export default function MarketplaceDetail() {
  const { packageId } = useParams<{ packageId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error, refresh } = useMarketplacePackageDetail(packageId);
  const {
    data: preview,
    isLoading: isPreviewLoading,
    error: previewError,
    loadPreview,
  } = useMarketplacePackagePreview(packageId);
  const { install, progress: installProgress, isInstalling } = useInstallPackage();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutInstallState, setCheckoutInstallState] = useState<MarketplacePackageDetail["installState"] | null>(null);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [isCompletingCheckout, setIsCompletingCheckout] = useState(false);
  const [excludedAssetIds, setExcludedAssetIds] = useState<string[]>([]);
  const [reviewRating, setReviewRating] = useState("5");
  const [reviewText, setReviewText] = useState("");
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewSuccess, setReviewSuccess] = useState<string | null>(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  const previewGroups = useMemo(() => {
    if (!preview) return [];
    const grouped = new Map<string, PreviewItem[]>();
    for (const item of preview.items) {
      const existing = grouped.get(item.assetType) ?? [];
      existing.push(item);
      grouped.set(item.assetType, existing);
    }
    return [...grouped.entries()];
  }, [preview]);

  const customizationSummary = useMemo(() => {
    if (!preview) return null;
    const excluded = new Set(excludedAssetIds);
    const includedCount = preview.items.filter((item) => !excluded.has(item.assetId)).length;
    return {
      excludedCount: excludedAssetIds.length,
      includedCount,
    };
  }, [excludedAssetIds, preview]);

  const clearCheckoutParam = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("marketplace_checkout");
    setSearchParams(next, { replace: true });
  };

  const effectiveInstallState = checkoutInstallState ?? data?.installState ?? null;
  const checkoutMode = Boolean(data && data.priceCents > 0 && effectiveInstallState?.status === "checkout_required");
  const resumeInstallMode = effectiveInstallState?.status === "already_purchased";

  useEffect(() => {
    if (!packageId || !data) return;

    const checkoutStatus = searchParams.get("marketplace_checkout");
    const storageKey = getCheckoutStorageKey(packageId);

    if (checkoutStatus === "cancel") {
      sessionStorage.removeItem(storageKey);
      setCheckoutError("Checkout was cancelled before the purchase completed.");
      clearCheckoutParam();
      return;
    }

    if (checkoutStatus !== "success" || isCompletingCheckout) return;

    const rawSession = sessionStorage.getItem(storageKey);
    if (!rawSession) {
      setCheckoutError("Hosted checkout returned without a stored marketplace session.");
      clearCheckoutParam();
      return;
    }

    let parsedSession: { providerId: "stripe" | "paypal" | "mollie" | "maib_bank"; providerSessionId: string; excludeAssetIds?: string[] } | null = null;
    try {
      parsedSession = JSON.parse(rawSession) as { providerId: "stripe" | "paypal" | "mollie" | "maib_bank"; providerSessionId: string; excludeAssetIds?: string[] };
    } catch {
      sessionStorage.removeItem(storageKey);
      setCheckoutError("Stored marketplace checkout session is invalid.");
      clearCheckoutParam();
      return;
    }

    setIsCompletingCheckout(true);
    setCheckoutError(null);

    void completeMarketplaceCheckout(
      packageId,
      parsedSession.providerId,
      parsedSession.providerSessionId,
      parsedSession.excludeAssetIds ?? [],
    )
      .then((result) => {
        sessionStorage.removeItem(storageKey);
        setCheckoutInstallState(result.installState);
        setExcludedAssetIds(parsedSession.excludeAssetIds ?? []);
        clearCheckoutParam();
      })
      .catch((err) => {
        setCheckoutError(err instanceof Error ? err.message : "Failed to complete marketplace checkout");
        clearCheckoutParam();
      })
      .finally(() => {
        setIsCompletingCheckout(false);
      });
  }, [clearCheckoutParam, data, isCompletingCheckout, packageId, searchParams]);

  useEffect(() => {
    if (!preview) return;
    const previewIds = new Set(preview.items.map((item) => item.assetId));
    setExcludedAssetIds((current) => current.filter((assetId) => previewIds.has(assetId)));
  }, [preview]);

  const toggleAssetSelection = (assetId: string) => {
    setExcludedAssetIds((current) => (
      current.includes(assetId)
        ? current.filter((value) => value !== assetId)
        : [...current, assetId]
    ));
  };

  const handleReviewSubmit = async () => {
    if (!packageId || !data?.reviewState.canReview) return;

    setReviewError(null);
    setReviewSuccess(null);
    setIsSubmittingReview(true);

    try {
      await submitMarketplaceReview(packageId, {
        rating: Number(reviewRating),
        reviewText: reviewText.trim() || undefined,
      });
      setReviewSuccess("Marketplace review submitted for moderation.");
      setReviewText("");
      await refresh();
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : "Failed to submit marketplace review.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleAcquirePackage = async () => {
    if (!data || !packageId || !effectiveInstallState) return;

    setCheckoutError(null);

    if (data.priceCents > 0 && effectiveInstallState.status === "checkout_required") {
      setIsStartingCheckout(true);
      try {
        const session = await startMarketplaceCheckout(packageId, excludedAssetIds);
        if (!session.checkoutUrl) {
          throw new Error("Hosted checkout URL is unavailable for this package.");
        }
        sessionStorage.setItem(getCheckoutStorageKey(packageId), JSON.stringify({
          providerId: session.providerId,
          providerSessionId: session.providerSessionId,
          excludeAssetIds: excludedAssetIds,
        }));
        window.location.assign(session.checkoutUrl);
      } catch (err) {
        setCheckoutError(err instanceof Error ? err.message : "Failed to start marketplace checkout");
      } finally {
        setIsStartingCheckout(false);
      }
      return;
    }

    if (effectiveInstallState.canInstall || resumeInstallMode) {
      const result = await install(packageId, excludedAssetIds);
      if (result?.installState) {
        setCheckoutInstallState(result.installState);
      }
    }
  };

  if (isLoading) return <DetailSkeleton />;

  if (error || !data) {
    return (
      <div className="space-y-4">
        <Link to="/marketplace" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          Back to marketplace
        </Link>
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="h-4 w-4" />
          Failed to load package detail.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link to="/marketplace" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />
        Back to marketplace
      </Link>

      <section className="rounded-2xl border border-border bg-background p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded-full border border-border px-2 py-0.5">{data.category ?? "General"}</span>
              <span className="rounded-full border border-border px-2 py-0.5">{data.packageSummary.totalAssets} assets</span>
              <span className="rounded-full border border-border px-2 py-0.5">{effectiveInstallState?.label ?? data.installState.label}</span>
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-foreground">{data.title}</h1>
              <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                {data.description ?? "Published package with mounted marketplace metadata."}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Star className="h-4 w-4 fill-amber-400 text-[var(--color-warning)]/90" />
                {formatNumber(data.avgRating)} from {formatNumber(data.reviewCount)} approved reviews
              </span>
              <span className="flex items-center gap-1.5">
                <BarChart3 className="h-4 w-4" />
                {formatNumber(data.installCount)} installs
              </span>
              <span className="flex items-center gap-1.5">
                <Package className="h-4 w-4" />
                Snapshot version {data.snapshotVersionId}
              </span>
              {data.publishedAt ? (
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" />
                  Published {formatDate(data.publishedAt)}
                </span>
              ) : null}
              {data.performancePreview?.isVerified ? (
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-[var(--color-primary)]" />
                  Verified install base
                </span>
              ) : null}
            </div>
          </div>

          <div className="w-full max-w-sm rounded-xl border border-border bg-muted/40 p-4">
            <div className="text-2xl font-semibold text-foreground">{formatMoney(data.priceCents, data.currency)}</div>
            <p className="mt-2 text-sm text-muted-foreground">{effectiveInstallState?.description ?? data.installState.description}</p>
            <button
              type="button"
              onClick={() => void loadPreview()}
              disabled={data.previewState.status === "unavailable" || isPreviewLoading}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--color-primary)]/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Eye className="h-4 w-4" />
              {isPreviewLoading ? "Loading preview..." : data.previewState.label}
            </button>
            {(effectiveInstallState?.canInstall || checkoutMode || resumeInstallMode) ? (
              <button
                type="button"
                onClick={() => void handleAcquirePackage()}
                disabled={isInstalling || isStartingCheckout || isCompletingCheckout || installProgress?.status === "complete"}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--color-primary)] bg-transparent px-4 py-2.5 text-sm font-medium text-[var(--color-primary)] transition-colors hover:bg-[var(--color-primary)]/10 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isStartingCheckout ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Redirecting to checkout...
                  </>
                ) : isCompletingCheckout ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Finalizing purchase...
                  </>
                ) : isInstalling ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Installing...
                  </>
                ) : installProgress?.status === "complete" ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Installed
                  </>
                ) : checkoutMode ? (
                  <>
                    <ShoppingCart className="h-4 w-4" />
                    Buy and install
                  </>
                ) : resumeInstallMode ? (
                  <>
                    <Download className="h-4 w-4" />
                    Resume install
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Install package
                  </>
                )}
              </button>
            ) : null}
            {checkoutError ? (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3 text-xs text-[var(--color-destructive)]">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {checkoutError}
              </div>
            ) : null}
            {installProgress?.status === "failed" && installProgress.error ? (
              <div className="mt-2 flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3 text-xs text-[var(--color-destructive)]">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {installProgress.error}
              </div>
            ) : null}
            <div className="mt-3 rounded-lg border border-border bg-background p-3 text-xs text-muted-foreground">
              <div className="font-medium text-foreground">What this purchase means</div>
              <ul className="mt-2 space-y-1">
                <li>
                  {formatOwnershipLabel(data.purchaseSemantics.assetOwnership)} from snapshot version{" "}
                  {data.purchaseSemantics.purchasedSnapshotVersionId}.
                </li>
                <li>
                  {formatUpdateLabel(data.purchaseSemantics.creatorUpdatePropagation)}. Existing installs stay{" "}
                  {data.purchaseSemantics.existingBuyerInstallsOnNewVersion.replaceAll("_", " ")}.
                </li>
                {effectiveInstallState?.status === "already_purchased" ? (
                  <li>
                    This tenant already owns the package. Use resume install to finish deployment instead of starting a new checkout.
                  </li>
                ) : null}
                <li>
                  Install mode: {data.canonicalModel.descriptor.installMode.replaceAll("_", " ")} via the canonical{" "}
                  {data.canonicalModel.descriptor.label.toLowerCase()} surface.
                </li>
                <li>
                  Publication visibility: {formatContractLabel(data.canonicalModel.descriptor.visibility)} with{" "}
                  {formatContractLabel(data.canonicalModel.descriptor.sourceOfTruth)} as the source of truth.
                </li>
                <li>
                  Package channel: {formatContractLabel(data.canonicalModel.descriptor.channel)}, granularity:{" "}
                  {formatContractLabel(data.canonicalModel.descriptor.granularity)}, publish target:{" "}
                  {formatContractLabel(data.canonicalModel.descriptor.publishTarget)}.
                </li>
                <li>
                  Distribution flags: {data.canonicalModel.descriptor.isPrivate ? "private" : "public"},{" "}
                  {data.canonicalModel.descriptor.isPublishable ? "publishable" : "not publishable"},{" "}
                  and {data.canonicalModel.descriptor.isDistributable ? "distributable" : "not distributable"}.
                </li>
                <li>
                  Canonical records: listing {data.canonicalModel.listingId}, snapshot {data.canonicalModel.snapshotId},
                  purchased snapshot {data.purchaseSemantics.purchasedSnapshotId}.
                </li>
                <li>
                  Canonical model: {formatContractLabel(data.canonicalModel.kind)} sourced from{" "}
                  {formatContractLabel(data.canonicalModel.sourceTemplateKind)}.
                </li>
                <li>
                  Legacy package-kit surfaces: {data.canonicalModel.descriptor.legacySurfaces.join(", ")}. Bridge status stays{" "}
                  {formatContractLabel(data.purchaseSemantics.legacyKitFlowStatus)}.
                </li>
              </ul>
            </div>
            <div className="mt-3 rounded-lg border border-border bg-background p-3 text-xs text-muted-foreground">
              <div className="font-medium text-foreground">Preview contract</div>
              <div className="mt-2">
                Status: {formatContractLabel(data.previewState.status)}. {data.previewState.description}
              </div>
            </div>
            <div className="mt-3 rounded-lg border border-border bg-background p-3 text-xs text-muted-foreground">
              Preview shows snapshot contents and counts from the published package version. It is not a live rendered sandbox or a completed install.
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <section className="rounded-2xl border border-border bg-background p-6">
          {data.screenshots.length > 0 ? (
            <div className="mb-6 space-y-3">
              <div>
                <h2 className="text-lg font-medium text-foreground">Package screenshots</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Review mounted screenshots from the published marketplace package before previewing or installing it.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {data.screenshots.map((screenshotUrl, index) => (
                  <figure
                    key={`${screenshotUrl}-${index}`}
                    className="overflow-hidden rounded-xl border border-border bg-muted/30"
                  >
                    <img
                      src={screenshotUrl}
                      alt={`${data.title} screenshot ${index + 1}`}
                      className="aspect-[4/3] w-full object-cover"
                    />
                    <figcaption className="border-t border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                      Screenshot {index + 1} of {data.screenshots.length}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          ) : null}

          <h2 className="text-lg font-medium text-foreground">Package contents</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Concrete asset counts from the published snapshot version.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {Object.entries(data.assetCounts).map(([assetType, count]) => (
              <div key={assetType} className="rounded-lg border border-border bg-muted/40 p-4">
                <div className="text-sm font-medium text-foreground">{assetType}</div>
                <div className="mt-1 text-2xl font-semibold text-foreground">{count}</div>
              </div>
            ))}
          </div>

          {preview ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                {preview.description}
              </div>
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background p-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-1">
                  <CheckSquare className="h-3.5 w-3.5" />
                  {customizationSummary?.includedCount ?? preview.items.length} included
                </span>
                <span className="rounded-full border border-border px-2 py-1">
                  {customizationSummary?.excludedCount ?? 0} excluded
                </span>
                <span className="rounded-full border border-border px-2 py-1">
                  Preview version: v{preview.versionNumber}
                </span>
                <span className="rounded-full border border-border px-2 py-1 font-mono">
                  Config hash: {preview.configHash}
                </span>
                <span className="rounded-full border border-border px-2 py-1">
                  Mode: {formatContractLabel(preview.mode)}
                </span>
                <span className="rounded-full border border-border px-2 py-1">
                  Payload: {formatBytes(preview.configSizeBytes)}
                </span>
                <span>Excluded assets will be skipped during direct install or hosted checkout completion.</span>
              </div>
              {previewGroups.map(([assetType, items]) => (
                <div key={assetType} className="rounded-lg border border-border">
                  <div className="border-b border-border bg-muted/30 px-4 py-3 text-sm font-medium text-foreground">
                    {assetType} ({items.length})
                  </div>
                  <div className="divide-y divide-border">
                    {items.map((item) => (
                      <label key={item.assetId} className="flex cursor-pointer items-start gap-3 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={!excludedAssetIds.includes(item.assetId)}
                          onChange={() => toggleAssetSelection(item.assetId)}
                          className="mt-1 h-4 w-4 rounded border-border text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-foreground">{item.name}</div>
                          {item.description ? (
                            <div className="mt-1 text-sm text-muted-foreground">{item.description}</div>
                          ) : null}
                          {excludedAssetIds.includes(item.assetId) ? (
                            <div className="mt-1 text-xs text-muted-foreground">Excluded from installation</div>
                          ) : null}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : previewError ? (
            <div className="mt-6 flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
              <AlertCircle className="h-4 w-4" />
              {previewError}
            </div>
          ) : null}
        </section>

        <section className="space-y-4">
          <div className="rounded-2xl border border-border bg-background p-6">
            <h2 className="text-lg font-medium text-foreground">Creator</h2>
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              <div className="font-medium text-foreground">{data.creatorProfile.name}</div>
              <div>{data.creatorProfile.totalListings} published listings</div>
              <div>{formatNumber(data.creatorProfile.avgRating)} average marketplace rating</div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-background p-6">
            <h2 className="text-lg font-medium text-foreground">Verified signals</h2>
            <div className="mt-4 space-y-3 text-sm text-muted-foreground">
              <div className="rounded-lg border border-border bg-muted/40 p-3">
                <div className="font-medium text-foreground">Install base</div>
                <div className="mt-1">{formatNumber(data.verifiedPerformance.totalInstalls)} installs recorded in marketplace purchases.</div>
              </div>
              <div className="rounded-lg border border-border bg-muted/40 p-3">
                <div className="font-medium text-foreground">Performance metrics</div>
                <div className="mt-1">
                  {data.verifiedPerformance.conversionRate || data.verifiedPerformance.emailOpenRate || data.verifiedPerformance.roas
                    ? "Mounted verified metrics are available for this listing."
                    : "No aggregate conversion, email, or ROAS benchmark is mounted for this listing yet. The surface falls back to install and review truth instead of inventing metrics."}
                </div>
                {data.verifiedPerformance.conversionRate || data.verifiedPerformance.emailOpenRate || data.verifiedPerformance.roas ? (
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-md border border-border/70 bg-background px-3 py-2">
                      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Conversion</div>
                      <div className="mt-1 text-sm font-semibold text-foreground">
                        {data.verifiedPerformance.conversionRate
                          ? `${formatPercent(data.verifiedPerformance.conversionRate.avg)} avg · ${formatPercent(data.verifiedPerformance.conversionRate.median)} median · ${formatPercent(data.verifiedPerformance.conversionRate.p90)} p90`
                          : "No mounted benchmark yet"}
                      </div>
                    </div>
                    <div className="rounded-md border border-border/70 bg-background px-3 py-2">
                      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Email open rate</div>
                      <div className="mt-1 text-sm font-semibold text-foreground">
                        {data.verifiedPerformance.emailOpenRate
                          ? `${formatPercent(data.verifiedPerformance.emailOpenRate.avg)} avg · ${formatPercent(data.verifiedPerformance.emailOpenRate.median)} median · ${formatPercent(data.verifiedPerformance.emailOpenRate.p90)} p90`
                          : "No mounted benchmark yet"}
                      </div>
                    </div>
                    <div className="rounded-md border border-border/70 bg-background px-3 py-2">
                      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">ROAS</div>
                      <div className="mt-1 text-sm font-semibold text-foreground">
                        {data.verifiedPerformance.roas
                          ? `${formatRatio(data.verifiedPerformance.roas.avg)} avg · ${formatRatio(data.verifiedPerformance.roas.median)} median`
                          : "No mounted benchmark yet"}
                      </div>
                    </div>
                  </div>
                ) : null}
                {data.verifiedPerformance.topPerformingAsset ? (
                  <div className="mt-3 rounded-md border border-border/70 bg-background px-3 py-2">
                    <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Top-performing asset</div>
                    <div className="mt-1 text-sm font-semibold text-foreground">
                      {data.verifiedPerformance.topPerformingAsset.name}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {formatMetricLabel(data.verifiedPerformance.topPerformingAsset.metric)} signal at{" "}
                      {data.verifiedPerformance.topPerformingAsset.metric === "roas"
                        ? formatRatio(data.verifiedPerformance.topPerformingAsset.value)
                        : formatPercent(data.verifiedPerformance.topPerformingAsset.value)}
                    </div>
                  </div>
                ) : null}
                <div className="mt-3 text-xs text-muted-foreground">
                  Verified metrics last computed {formatDate(data.verifiedPerformance.lastComputedAt)}.
                </div>
              </div>
            </div>
          </div>

          {data.recentReviews.length > 0 ? (
            <div className="rounded-2xl border border-border bg-background p-6">
              <h2 className="text-lg font-medium text-foreground">Recent reviews</h2>
              <div className="mt-2 text-sm text-muted-foreground">
                Approved buyer reviews already published to the marketplace.
              </div>
              <div className="mt-4 space-y-3">
                {data.recentReviews.map((review, index) => (
                  <div key={`${review.createdAt}-${index}`} className="rounded-lg border border-border bg-muted/40 p-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                      <span className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-amber-400 text-[var(--color-warning)]/90" />
                        {formatNumber(review.rating)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Published {formatDate(review.createdAt)}
                      </span>
                    </div>
                    {review.text ? (
                      <p className="mt-2 text-sm text-muted-foreground">{review.text}</p>
                    ) : (
                      <p className="mt-2 text-sm text-muted-foreground">Rating submitted without review text.</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-border bg-background p-6">
            <h2 className="text-lg font-medium text-foreground">Your review</h2>
            <div className="mt-2 text-sm text-muted-foreground">{data.reviewState.description}</div>

            {data.viewerReview ? (
              <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4">
                <div className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                  <span className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-amber-400 text-[var(--color-warning)]/90" />
                    {formatNumber(data.viewerReview.rating)}
                  </span>
                  <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                    {data.reviewState.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Submitted {formatDate(data.viewerReview.createdAt)}
                  </span>
                </div>
                {data.viewerReview.text ? (
                  <p className="mt-2 text-sm text-muted-foreground">{data.viewerReview.text}</p>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">Rating submitted without review text.</p>
                )}
              </div>
            ) : data.reviewState.canReview ? (
              <div className="mt-4 space-y-3">
                <label className="block space-y-1">
                  <span className="text-sm font-medium text-foreground">Rating</span>
                  <select
                    value={reviewRating}
                    onChange={(event) => setReviewRating(event.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  >
                    {[5, 4, 3, 2, 1].map((value) => (
                      <option key={value} value={String(value)}>
                        {value} star{value === 1 ? "" : "s"}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block space-y-1">
                  <span className="text-sm font-medium text-foreground">Review</span>
                  <textarea
                    value={reviewText}
                    onChange={(event) => setReviewText(event.target.value)}
                    rows={4}
                    maxLength={2000}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                    placeholder="Share what worked after installing this package."
                  />
                </label>
                {reviewError ? (
                  <div className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3 text-xs text-[var(--color-destructive)]">
                    {reviewError}
                  </div>
                ) : null}
                {reviewSuccess ? (
                  <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-3 text-xs text-[var(--color-success)]">
                    {reviewSuccess}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => void handleReviewSubmit()}
                  disabled={isSubmittingReview}
                  className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-primary)]/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmittingReview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
                  Submit review
                </button>
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                {data.reviewState.label}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
